<template>
  <div class="dark-theme-wrapper">
    <div class="container py-5">
      <h1 class="main-title text-center mb-5">NASTAVENÍ APLIKACE</h1>

      <!-- KARTA 1: VIDITELNÉ KOLO -->
      <div class="admin-card mx-auto mb-5">
        <div class="card-header text-center mb-4">
          <svg
            class="icon-lg mb-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="3"></circle>
            <path
              d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
            ></path>
          </svg>

          <h2 class="section-title">VIDITELNÉ KOLO</h2>
          <p class="text-muted small">
            Toto nastavení určuje, které kolo se zobrazí uživatelům na úvodní
            stránce a v tipování.
          </p>
        </div>

        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-danger" role="status"></div>
        </div>

        <div v-else class="settings-form">
          <label class="form-label">VYBERTE AKTIVNÍ KOLO</label>

          <div class="input-group mb-4">
            <span class="input-icon-wrapper">
              <svg
                class="icon-sm"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="8" y1="6" x2="21" y2="6"></line>
                <line x1="8" y1="12" x2="21" y2="12"></line>
                <line x1="8" y1="18" x2="21" y2="18"></line>
                <line x1="3" y1="6" x2="3.01" y2="6"></line>
                <line x1="3" y1="12" x2="3.01" y2="12"></line>
                <line x1="3" y1="18" x2="3.01" y2="18"></line>
              </svg>
            </span>
            <select v-model="selectedRound" class="form-select dark-input">
              <option
                v-for="round in availableRounds"
                :key="round"
                :value="round"
              >
                {{ round }}. KOLO
              </option>
            </select>
          </div>

          <div class="current-status mb-4 text-center">
            <span class="text-muted">Aktuálně nastaveno:</span>
            <span class="status-badge">{{ loadedRound }}. KOLO</span>
          </div>

          <button
            @click="updateVisibleRound"
            class="btn-red-glow w-100"
            :disabled="saving || selectedRound === loadedRound"
          >
            <span
              v-if="saving"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ saving ? "UKLÁDÁM..." : "ULOŽIT ZMĚNU" }}
          </button>
        </div>

        <transition name="fade">
          <div
            v-if="successMessage"
            class="alert-box success mt-4 d-flex align-items-center justify-content-center"
          >
            <svg
              class="icon-sm me-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            {{ successMessage }}
          </div>
        </transition>
      </div>

      <!-- KARTA 2: NOVÁ SEZÓNA (RESET) -->
      <div class="admin-card mx-auto border-warning">
        <div class="card-header text-center mb-4">
          <svg
            class="icon-lg mb-3"
            style="stroke: #ffc107"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            ></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>

          <h2 class="section-title text-warning">RESET DO NOVÉ SEZÓNY</h2>
          <p class="text-muted small">
            Tato akce u všech uživatelů vynuluje body, vymaže historii kol a
            obnoví možnost použití jokera.
            <strong>Samotné účty zůstanou zachovány.</strong>
          </p>
        </div>

        <button
          @click="resetNewSeason"
          class="btn-warning-glow w-100"
          :disabled="resetting"
        >
          <span
            v-if="resetting"
            class="spinner-border spinner-border-sm me-2 text-dark"
          ></span>
          {{ resetting ? "PROBÍHÁ RESET..." : "VYNULOVAT VÝSLEDKY" }}
        </button>

        <transition name="fade">
          <div
            v-if="resetSuccessMessage"
            class="alert-box success mt-4 d-flex align-items-center justify-content-center"
          >
            <svg
              class="icon-sm me-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            {{ resetSuccessMessage }}
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from "../firebase";
// DŮLEŽITÉ: Přidány importy deleteField a writeBatch!
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  deleteField,
  writeBatch,
} from "firebase/firestore";

export default {
  name: "AdminSettings",
  data() {
    return {
      selectedRound: 1,
      loadedRound: 1,
      availableRounds: [],
      successMessage: "",
      loading: true,
      saving: false,

      // Proměnné pro reset sezóny
      resetting: false,
      resetSuccessMessage: "",
    };
  },
  async mounted() {
    await this.initData();
  },
  methods: {
    async initData() {
      this.loading = true;
      await Promise.all([
        this.fetchAvailableRounds(),
        this.fetchCurrentRound(),
      ]);
      this.loading = false;
    },

    async fetchAvailableRounds() {
      try {
        const querySnapshot = await getDocs(collection(db, "matches"));
        const rounds = new Set(
          querySnapshot.docs.map((doc) => doc.data().roundNumber)
        );

        if (rounds.size === 0) {
          this.availableRounds = Array.from({ length: 30 }, (_, i) => i + 1);
        } else {
          this.availableRounds = Array.from(rounds).sort((a, b) => a - b);
        }
      } catch (error) {
        console.error("Chyba při načítání dostupných kol:", error);
      }
    },

    async fetchCurrentRound() {
      try {
        const roundRef = doc(db, "settings", "visibleRound");
        const roundSnap = await getDoc(roundRef);
        if (roundSnap.exists()) {
          this.selectedRound = roundSnap.data().roundNumber;
          this.loadedRound = roundSnap.data().roundNumber;
        }
      } catch (error) {
        console.error("Chyba při načítání aktuálního kola:", error);
      }
    },

    async updateVisibleRound() {
      this.saving = true;
      this.successMessage = "";
      try {
        await setDoc(doc(db, "settings", "visibleRound"), {
          roundNumber: this.selectedRound,
        });

        this.loadedRound = this.selectedRound;
        this.successMessage = "Viditelné kolo bylo úspěšně aktualizováno!";
        setTimeout(() => (this.successMessage = ""), 3000);
      } catch (error) {
        console.error("Chyba při aktualizaci viditelného kola:", error);
        alert("Chyba při ukládání: " + error.message);
      } finally {
        this.saving = false;
      }
    },

    // NOVÁ FUNKCE PRO RESET SEZÓNY
    async resetNewSeason() {
      // 1. Bezpečnostní pojistka
      const confirmText = prompt(
        "POZOR: Tato akce vynuluje body a historii všem uživatelům pro novou sezónu!\n\nPro potvrzení napište slovo: RESET"
      );

      if (confirmText !== "RESET") {
        alert(
          "Bezpečnostní kontrola selhala nebo byla zrušena. K resetu nedošlo."
        );
        return;
      }

      this.resetting = true;
      this.resetSuccessMessage = "";

      try {
        // 2. Získání všech uživatelů (předpokládáme název kolekce "users")
        const usersSnapshot = await getDocs(collection(db, "users"));

        // 3. Vytvoření dávky (batch) pro hromadný update
        const batch = writeBatch(db);

        usersSnapshot.forEach((userDoc) => {
          const userRef = doc(db, "users", userDoc.id);

          // Přidání updatů do dávky (obdoba tvého Python skriptu)
          batch.update(userRef, {
            jokerUsed: false,
            jokerUsedRound: deleteField(), // Smaže pole z dokumentu
            totalPoints: 0,
            rounds: {}, // Vyprázdní mapu/objekt s koly
          });
        });

        // 4. Odeslání všech změn do databáze najednou
        await batch.commit();

        this.resetSuccessMessage =
          "Všichni uživatelé byli úspěšně připraveni na novou sezónu!";
        setTimeout(() => (this.resetSuccessMessage = ""), 5000);
      } catch (error) {
        console.error("Chyba při resetování uživatelů:", error);
        alert("Chyba při provádění resetu: " + error.message);
      } finally {
        this.resetting = false;
      }
    },
  },
};
</script>

<style scoped>
/* =========================================
   DARK THEME STYLING
   ========================================= */

.dark-theme-wrapper {
  background-color: #000000;
  min-height: 100vh;
  color: #ffffff;
  font-family: "Roboto", sans-serif;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: 2px;
  color: #fff;
  text-transform: uppercase;
}

/* Karta */
.admin-card {
  background: #111;
  border: 1px solid #333;
  border-radius: 15px;
  padding: 2.5rem;
  max-width: 600px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
}

.border-warning {
  border-color: #ffc107 !important;
  box-shadow: 0 0 20px rgba(255, 193, 7, 0.15);
}

.text-warning {
  color: #ffc107 !important;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 1px;
}

/* Ikony */
.icon-lg {
  width: 48px;
  height: 48px;
  stroke: #dc3545;
}

.icon-sm {
  width: 20px;
  height: 20px;
}

/* Formulář */
.form-label {
  font-size: 0.8rem;
  font-weight: bold;
  color: #888;
  letter-spacing: 1px;
  margin-bottom: 8px;
  display: block;
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon-wrapper {
  position: absolute;
  left: 15px;
  color: #666;
  z-index: 2;
  display: flex;
  align-items: center;
  pointer-events: none;
}

.dark-input {
  background-color: #000;
  border: 1px solid #444;
  color: #fff;
  padding: 12px 12px 12px 45px;
  border-radius: 8px;
  width: 100%;
  appearance: none;
  cursor: pointer;
  transition: all 0.3s;
}

.dark-input:focus {
  border-color: #dc3545;
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.2);
}

/* Status Badge */
.status-badge {
  display: inline-block;
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  padding: 5px 12px;
  border-radius: 20px;
  font-weight: bold;
  margin-left: 8px;
  border: 1px solid #28a745;
}

/* Tlačítka */
.btn-red-glow {
  background: linear-gradient(135deg, #a71d2a 0%, #dc3545 100%);
  color: white;
  padding: 15px;
  border: none;
  border-radius: 30px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4);
  font-size: 1.1rem;
}

.btn-red-glow:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.6);
}

.btn-red-glow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  background: #333;
  box-shadow: none;
}

/* Žluté tlačítko pro reset */
.btn-warning-glow {
  background: linear-gradient(135deg, #d39e00 0%, #ffc107 100%);
  color: #000;
  padding: 15px;
  border: none;
  border-radius: 30px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 193, 7, 0.4);
  font-size: 1.1rem;
}

.btn-warning-glow:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 193, 7, 0.6);
}

.btn-warning-glow:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  background: #665000;
  color: #999;
  box-shadow: none;
}

/* Alerty */
.alert-box {
  padding: 15px;
  border-radius: 8px;
  font-weight: bold;
}

.alert-box.success {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid #28a745;
}

/* Animace */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
